import React from 'react';
import { useNavigate } from 'react-router-dom';
import type { Venue } from '@/types/venue';
import { IconMapPin as MapPin, IconPlus as Plus, IconArmchair as Armchair, IconEdit as SquarePen, IconLock as Lock, IconWorld as Globe, IconEye as Eye } from '@tabler/icons-react';
import { Dropdown, DropdownTrigger, DropdownMenu, DropdownItem } from '@heroui/react';
import Trash from '@/icons/Trash';

const iconPill = 'inline-flex h-10 w-10 items-center justify-center rounded-full ring-1 transition-colors';

interface VenueListProps {
    venues: Venue[];
    isLoading: boolean;
    selectedId: string | null;
    onSelect: (id: string) => void;
    onEdit: (venue: Venue) => void;
    onDelete: (id: string) => void;
    onPreview: (venue: Venue) => void;
}

function countSeats(seatMap: any): number {
    if (!seatMap?.zones) return 0;
    return seatMap.zones.reduce(
        (zoneTotal: number, zone: any) =>
            zoneTotal + (zone.rows || []).reduce((rowTotal: number, row: any) => rowTotal + (row.seats?.length || 0), 0),
        0
    );
}

const VenueList: React.FC<VenueListProps> = ({ venues, isLoading, selectedId, onSelect, onEdit, onDelete, onPreview }) => {
    const navigate = useNavigate();

    if (isLoading) {
        return (
            <div className="flex flex-col gap-3">
                {[1, 2, 3].map(i => (
                    <div key={i} className="rounded-[28px] bg-white ring-1 ring-black/[0.06] dark:bg-white/[0.04] dark:ring-white/10 p-5 flex gap-4 animate-pulse">
                        <div className="w-20 h-20 rounded-2xl bg-grey95 dark:bg-white/10" />
                        <div className="flex-1 space-y-3 pt-2">
                            <div className="h-5 rounded-full bg-grey95 dark:bg-white/10 w-40 max-w-full" />
                            <div className="h-4 rounded-full bg-grey95 dark:bg-white/10 w-64 max-w-full" />
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    if (venues.length === 0) {
        return (
            <div className="rounded-[28px] bg-grey99 dark:bg-white/[0.04] flex flex-col items-center justify-center py-20 px-6 text-center">
                <span className="w-16 h-16 rounded-full bg-white ring-1 ring-black/[0.06] dark:bg-white/[0.06] dark:ring-white/10 text-grey60 dark:text-nightMuted flex items-center justify-center mb-5">
                    <MapPin className="w-7 h-7" />
                </span>
                <div className="text-[clamp(24px,3vw,32px)] font-bold tracking-[-0.04em] text-grey10 dark:text-white mb-2">No venues found</div>
                <div className="text-base text-grey40 dark:text-nightMuted">Add your first venue to see it listed here.</div>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-3">
            {venues.map((venue) => {
                const seats = countSeats(venue.seatMap);
                const isSelected = selectedId === venue._id;
                const canEdit = venue.canEdit !== false;
                const isPrivate = venue.visibility === 'selected';
                const stop = (e: React.SyntheticEvent) => e.stopPropagation();
                const seatmapButton = canEdit ? (
                    <button
                        className="flex items-center justify-center gap-2 h-10 px-4 text-sm font-semibold text-grey10 dark:text-white ring-1 ring-grey90 dark:ring-white/15 rounded-full hover:bg-grey99 dark:hover:bg-white/[0.06] transition-colors w-full sm:w-auto whitespace-nowrap"
                        onClick={(e) => { stop(e); navigate(`/seat-map-editor?venue=${venue._id}`); }}
                    >
                        {venue.seatMap ? <SquarePen className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
                        {venue.seatMap ? (venue.editingLocked ? 'View Seatmap' : 'Edit Seatmap') : 'Add Seatmap'}
                    </button>
                ) : seats > 0 && (
                    <button
                        className="flex items-center justify-center gap-2 h-10 px-4 text-sm font-semibold text-grey10 dark:text-white ring-1 ring-grey90 dark:ring-white/15 rounded-full hover:bg-grey99 dark:hover:bg-white/[0.06] transition-colors w-full sm:w-auto whitespace-nowrap"
                        onClick={(e) => { stop(e); onPreview(venue); }}
                    >
                        <Eye className="w-3.5 h-3.5" />
                        Preview Seatmap
                    </button>
                );

                return (
                    <div
                        key={venue._id}
                        onClick={() => onSelect(venue._id)}
                        className={`cursor-pointer rounded-[28px] p-4 sm:p-5 bg-white dark:bg-white/[0.04] transition-all ${isSelected ? 'ring-2 ring-primary dark:ring-nightAccent' : 'ring-1 ring-black/[0.06] hover:ring-black/[0.12] dark:ring-white/10 dark:hover:ring-white/20'}`}
                    >
                        <div className="flex items-start gap-4">
                            <span className="w-14 h-14 sm:w-20 sm:h-20 rounded-2xl bg-grey99 dark:bg-white/[0.06] text-grey50 dark:text-nightMuted flex items-center justify-center flex-shrink-0">
                                <MapPin className="w-6 h-6 sm:w-7 sm:h-7" />
                            </span>
                            <div className="min-w-0 flex-1 flex flex-col sm:flex-row sm:justify-between gap-3">
                                <div className="min-w-0 flex flex-col justify-between gap-3">
                                    <div className="flex items-center gap-2 min-w-0">
                                        <h3 className="text-lg font-bold tracking-[-0.02em] text-grey10 dark:text-white truncate">{venue.name}</h3>
                                        <span
                                            title={isPrivate ? 'Only organizers chosen by the owner can find and use this venue' : canEdit ? 'Any organizer can find and use this venue. Only your organization\'s owner, admins and event managers can edit it' : 'Shared by another organizer. Using it for an event creates an event copy you can customize'}
                                            className="flex items-center gap-1 flex-shrink-0 px-2.5 py-1 rounded-full text-xs font-semibold bg-grey99 text-grey30 dark:bg-white/[0.06] dark:text-nightTextSecondary"
                                        >
                                            {isPrivate ? <Lock className="w-3 h-3" /> : <Globe className="w-3 h-3" />}
                                            {isPrivate ? 'Selected organizers' : canEdit ? 'All organizers' : 'Shared · view only'}
                                        </span>
                                    </div>
                                    <div className="flex items-center flex-wrap gap-x-4 gap-y-1 text-sm text-grey40 dark:text-nightMuted">
                                        <span className="flex items-center gap-1.5 min-w-0">
                                            <MapPin className="w-4 h-4 flex-shrink-0" />
                                            <span className="truncate">{venue.address?.fullAddress}</span>
                                        </span>
                                        {seats > 0 && (
                                            <span className="flex items-center gap-1.5">
                                                <Armchair className="w-4 h-4 flex-shrink-0" />
                                                {seats} Seats
                                            </span>
                                        )}
                                        {venue.editingLocked ? (
                                            <span
                                                className="flex items-center gap-1 flex-shrink-0 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300"
                                                title="Editing is locked because tickets have already been sold for this event."
                                            >
                                                <Lock className="w-3 h-3" />
                                                Editing locked
                                            </span>
                                        ) : canEdit && !venue.seatMap && (
                                            <span className="text-xs text-grey40 dark:text-nightMuted">The venue map and tiers lock after the first ticket is sold.</span>
                                        )}
                                    </div>
                                </div>
                                {canEdit && <div className="hidden sm:flex flex-col items-end justify-between gap-3 flex-shrink-0">
                                    <div className="flex items-center gap-2">
                                        <button
                                            aria-label="Delete venue"
                                            disabled={venue.editingLocked || venue.seatsLocked}
                                            title={venue.editingLocked || venue.seatsLocked ? "Can't delete: tickets have been sold at this venue" : 'Delete venue'}
                                            className={`${iconPill} text-red-500 ring-red-500/25 hover:bg-red-500/10 hover:text-red-600 disabled:cursor-not-allowed disabled:text-grey80 disabled:ring-grey95 disabled:hover:bg-transparent dark:disabled:text-nightMuted/40 dark:disabled:ring-white/10`}
                                            onClick={(e) => { stop(e); onDelete(venue._id); }}
                                        >
                                            <Trash className="w-5 h-5" />
                                        </button>
                                        <button aria-label="Edit venue" className={`${iconPill} text-grey10 ring-grey90 hover:bg-grey99 dark:text-white dark:ring-white/15 dark:hover:bg-white/[0.06]`} onClick={(e) => { stop(e); onEdit(venue); }}>
                                            <SquarePen className="w-5 h-5" />
                                        </button>
                                    </div>
                                    {seatmapButton}
                                </div>}
                                {!canEdit && seatmapButton && <div className="hidden sm:flex items-end flex-shrink-0">{seatmapButton}</div>}
                            </div>
                            {canEdit && <div className="sm:hidden" onClick={stop}>
                                <Dropdown>
                                    <DropdownTrigger>
                                        <button aria-label="Venue actions" className="inline-flex h-9 w-9 items-center justify-center rounded-full text-xl leading-none text-grey30 hover:bg-grey99 dark:text-nightTextSecondary dark:hover:bg-white/[0.06]">⋮</button>
                                    </DropdownTrigger>
                                    <DropdownMenu aria-label="Venue actions">
                                        <DropdownItem key="edit" onPress={() => onEdit(venue)}>Edit</DropdownItem>
                                        <DropdownItem
                                            key="delete"
                                            className="text-danger"
                                            color="danger"
                                            isDisabled={venue.editingLocked || venue.seatsLocked}
                                            description={venue.editingLocked || venue.seatsLocked ? 'Tickets have been sold' : undefined}
                                            onPress={() => onDelete(venue._id)}
                                        >
                                            Delete
                                        </DropdownItem>
                                    </DropdownMenu>
                                </Dropdown>
                            </div>}
                        </div>
                        {seatmapButton && <div className="sm:hidden mt-4">{seatmapButton}</div>}
                    </div>
                );
            })}
        </div>
    );
};

export default VenueList;
